import { DeleteOutline, VisibilityOutlined } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Stack,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";

import { PatientSupportingDocumentRecord } from "./types";

type DocStatus = "pending" | "approved" | "rejected";

const DOC_STATUS_META: Record<
  DocStatus,
  { label: string; color: "default" | "info" | "success" | "error" }
> = {
  pending: { label: "Aguardando", color: "info" },
  approved: { label: "Verificado", color: "success" },
  rejected: { label: "Recusado", color: "error" },
};

function resolveDocStatus(document: PatientSupportingDocumentRecord): DocStatus {
  if (document.isAttested) return "approved";
  if (document.isRejected) return "rejected";
  return "pending";
}

function docDisplayTitle(document: PatientSupportingDocumentRecord, index: number): string {
  return document.description?.trim() || document.fileName || `Arquivo ${index + 1}`;
}

function docMetaLine(document: PatientSupportingDocumentRecord, status: DocStatus): string | null {
  if (status === "approved" && document.attestedAt) {
    return `Verificado em ${dayjs(document.attestedAt).format("DD/MM/YYYY")}`;
  }
  if (status === "rejected") {
    return document.rejectionReason || "Recusado pela loja";
  }
  const sentAt = document.sentAt || document.updatedAt;
  return sentAt
    ? `Enviado em ${dayjs(sentAt).format("DD/MM/YYYY")}`
    : null;
}

function docFileLabel(document: PatientSupportingDocumentRecord): string {
  const contentType = document.mimeType?.toLowerCase() ?? "";
  if (contentType.includes("pdf")) return "PDF";
  if (contentType.includes("png")) return "PNG";
  if (contentType.includes("jpeg") || contentType.includes("jpg")) return "JPG";
  if (contentType.includes("webp")) return "WEBP";

  const fileName = document.fileName?.trim();
  if (!fileName) return "Documento";
  if (fileName.length <= 22) return fileName;
  return `${fileName.slice(0, 19)}…`;
}

interface PatientSupportingDocumentTileProps {
  document: PatientSupportingDocumentRecord;
  index: number;
  onView: (document: PatientSupportingDocumentRecord) => void;
  onRemove: (document: PatientSupportingDocumentRecord) => void;
  onReplace?: (document: PatientSupportingDocumentRecord) => void;
}

/** Tile de documento — espelha o layout da verificação profissional (DocumentsChecklist). */
export function PatientSupportingDocumentTile({
  document,
  index,
  onView,
  onRemove,
  onReplace,
}: PatientSupportingDocumentTileProps) {
  const title = docDisplayTitle(document, index);
  const statusKey = resolveDocStatus(document);
  const statusMeta = DOC_STATUS_META[statusKey];
  const metaLine = docMetaLine(document, statusKey);
  const fileLabel = docFileLabel(document);
  const rejected = statusKey === "rejected";
  const verified = statusKey === "approved";

  return (
    <Box
      sx={{
        border: 1,
        borderColor: document.isExpired
          ? "warning.main"
          : rejected
            ? "error.light"
            : "grey.200",
        borderRadius: 1,
        bgcolor: "background.paper",
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        minHeight: 132,
        minWidth: 220,
      }}
    >
      <Box
        role="button"
        tabIndex={0}
        aria-label={`Visualizar ${title}`}
        onClick={() => onView(document)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            onView(document);
          }
        }}
        sx={{
          position: "relative",
          flex: 1,
          minHeight: 72,
          px: 1.25,
          py: 1,
          cursor: "pointer",
          outline: "none",
          overflow: "hidden",
          "&:hover .doc-preview-overlay": {
            opacity: 1,
          },
          "&:hover .doc-preview-content": {
            opacity: 0,
          },
          "&:focus-visible": {
            boxShadow: (theme) =>
              `inset 0 0 0 2px ${theme.palette.primary.main}`,
          },
        }}
      >
        <Box
          className="doc-preview-overlay"
          aria-hidden
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            bgcolor: "rgba(0, 0, 0, 0.52)",
            opacity: 0,
            transition: "opacity 0.18s ease",
            pointerEvents: "none",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.5,
          }}
        >
          <VisibilityOutlined sx={{ fontSize: 32, color: "common.white" }} />
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{
              color: "common.white",
              letterSpacing: 0.3,
              textShadow: "0 1px 2px rgba(0,0,0,0.35)",
            }}
          >
            Visualizar
          </Typography>
        </Box>

        <Box
          className="doc-preview-content"
          sx={{
            position: "relative",
            zIndex: 1,
            transition: "opacity 0.18s ease",
          }}
        >
          <Typography
            variant="caption"
            fontWeight={600}
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              lineHeight: 1.4,
            }}
          >
            {title}
          </Typography>
          {metaLine ? (
            <Typography
              variant="caption"
              color={
                rejected
                  ? "error.main"
                  : verified
                    ? "success.main"
                    : "text.secondary"
              }
              display="block"
              sx={{
                mt: 0.5,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: rejected ? "normal" : "nowrap",
                ...(rejected ? { WebkitLineClamp: 3, display: "-webkit-box", WebkitBoxOrient: "vertical" } : {}),
              }}
            >
              {metaLine}
            </Typography>
          ) : null}
        </Box>
      </Box>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={0.75}
        sx={{
          flexShrink: 0,
          px: 1,
          py: 0.75,
          minHeight: 44,
          borderTop: 1,
          borderColor: "grey.100",
          bgcolor: "grey.50",
        }}
      >
        <Typography
          variant="caption"
          color="text.secondary"
          noWrap
          sx={{ flexShrink: 0, maxWidth: "38%" }}
        >
          {fileLabel}
        </Typography>

        <Stack
          direction="row"
          spacing={0.75}
          alignItems="center"
          sx={{ flex: 1, justifyContent: "flex-end", minWidth: 0 }}
          onClick={(event) => event.stopPropagation()}
        >
          <Chip
            size="small"
            label={document.isExpired ? "Vencido" : statusMeta.label}
            color={document.isExpired ? "warning" : statusMeta.color}
            variant="outlined"
            sx={{
              height: 24,
              flexShrink: 0,
              "& .MuiChip-label": { px: 0.75, fontSize: "0.68rem" },
            }}
          />
          {(rejected || document.isExpired) && onReplace ? (
            <Button
              size="small"
              variant="contained"
              color={rejected ? "error" : "warning"}
              onClick={() => onReplace(document)}
              sx={{
                textTransform: "none",
                minWidth: 0,
                px: 1,
                py: 0.375,
                fontSize: "0.72rem",
              }}
            >
              Substituir
            </Button>
          ) : !verified ? (
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<DeleteOutline sx={{ fontSize: 16 }} />}
              onClick={() => onRemove(document)}
              sx={{
                textTransform: "none",
                minWidth: 0,
                px: 1,
                py: 0.375,
                fontSize: "0.72rem",
                lineHeight: 1.3,
                bgcolor: "background.paper",
                boxShadow: 1,
              }}
            >
              Remover
            </Button>
          ) : null}
        </Stack>
      </Stack>
    </Box>
  );
}

export type PatientDocumentCategoryStatus =
  | "empty"
  | "pending"
  | "approved"
  | "rejected"
  | "mixed";

const CATEGORY_STATUS_META: Record<
  PatientDocumentCategoryStatus,
  { label: string; color: "default" | "info" | "success" | "error" }
> = {
  empty: { label: "Não enviado", color: "default" },
  pending: { label: "Aguardando", color: "info" },
  approved: { label: "Verificado", color: "success" },
  rejected: { label: "Recusado", color: "error" },
  mixed: { label: "Parcial", color: "info" },
};

export function resolvePatientDocumentCategoryStatus(
  documents: PatientSupportingDocumentRecord[],
): PatientDocumentCategoryStatus {
  if (!documents.length) return "empty";

  let approved = 0;
  let rejected = 0;

  for (const document of documents) {
    const status = resolveDocStatus(document);
    if (status === "approved") approved += 1;
    else if (status === "rejected") rejected += 1;
  }

  if (approved === documents.length) return "approved";
  if (rejected === documents.length) return "rejected";
  if (approved === 0 && rejected === 0) return "pending";
  return "mixed";
}

export function getPatientDocumentCategoryStatusMeta(
  status: PatientDocumentCategoryStatus,
) {
  return CATEGORY_STATUS_META[status];
}
